import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import {
	FormViewMode,
	useFormIndexActions,
	useSelectedFormStoreState,
} from '@atlassian/proforma-form-system';
import { Action, ActionSubject, FormUpdateType } from '@atlassian/proforma-utils/analytics';
import { useShowNotice } from '@atlassian/proforma-utils/show-notice';
import type { ProjectType } from '@atlassian/proforma-utils/types';
import { useSelectFormNoDiscardChangeNotice } from '../use-handle-form-selected/index.tsx';
import { messages } from './messages.tsx';
import { CloseFormResult, type HandleCloseForm } from './types.tsx';

/**
 * Runs validation checks on a form, and submits it if validation passes, closing the form for customer edits.
 *
 * If validation fails, the form will be expanded and set to edit mode, so that the validation errors will be visible to
 * the user. The handler will reject with an error if it is called when a form is currently in edit mode, to prevent the
 * handler from forcibly discarding unsaved edits to the form.
 *
 * @returns A promise that resolves with one of:
 * - CloseFormResult.Success: The form was closed successfully.
 * - CloseFormResult.Cancelled: The form was not closed, because the user dismissed the confirmation modal.
 * - CloseFormResult.ValidationFailed: The form was not closed, because the form contains validation errors.
 * - CloseFormResult.UnknownError: The form was not closed, because of an API error during form submission.
 */
export const useHandleCloseForm = (
	loadFormStore: (id: number) => Promise<FormStore>,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
	projectType: ProjectType,
): HandleCloseForm => {
	const { showNotice } = useShowNotice();
	const { selectedFormStore } = useSelectedFormStoreState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectFormNoDiscardChangeNotice = useSelectFormNoDiscardChangeNotice(
		loadFormStore,
		disableIssueViewFullPageDropzoneCallback,
	);
	const { updateFormStatus, setFormViewMode, submitForm, reportError } = useFormIndexActions();

	return async (formId: number, formStore?: FormStore) => {
		if (selectedFormStore?.answersModified) {
			throw new Error('"Close form edits" not allowed while editing form');
		}
		const loadedFormStore = formStore || (await loadFormStore(formId));

		loadedFormStore.validate();
		if (loadedFormStore.invalid) {
			await selectFormNoDiscardChangeNotice(formId, loadedFormStore);

			setFormViewMode(FormViewMode.Edit);

			return CloseFormResult.ValidationFailed;
		}

		return new Promise((resolve) => {
			showNotice({
				title:
					projectType === 'service_desk' ? (
						<FormattedMessage
							{...messages.closeModalTitle}
							values={{ formName: loadedFormStore.formName }}
						/>
					) : (
						<FormattedMessage
							{...messages.closeFormModalTitle}
							values={{ formName: loadedFormStore.formName }}
						/>
					),
				body:
					projectType === 'service_desk' ? (
						<FormattedMessage {...messages.closeModalBody} />
					) : (
						<FormattedMessage {...messages.closeFormModalBody} />
					),
				closeText: <FormattedMessage {...messages.closeModalCancel} />,
				confirmText: <FormattedMessage {...messages.closeModalConfirm} />,
				onCloseHandler: () => resolve(CloseFormResult.Cancelled),
				onConfirmHandler: async () => {
					try {
						if (loadedFormStore.formId) {
							const isCurrentFormSelected = !!formStore;
							const updatedForm = await submitForm(
								loadedFormStore.formId,
								loadedFormStore.submitLock,
							);
							if (isCurrentFormSelected) {
								await loadedFormStore.update(updatedForm);
								loadedFormStore.hideValidations();
								setFormViewMode(FormViewMode.View);
							}
							updateFormStatus(loadedFormStore.formId, updatedForm.state.status);

							fireTrackAnalytics(
								createAnalyticsEvent({}),
								`${ActionSubject.FORM} ${Action.UPDATED}`,
								{
									formId: loadedFormStore.formId,
									templateFormId: loadedFormStore.templateFormId,
									formUpdateType: FormUpdateType.CLOSE,
									visibility: loadedFormStore.internal ? 'internal' : 'external',
								},
							);
						}

						resolve(CloseFormResult.Success);
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (e: any) {
						reportError(e, {
							id: 'closeForm',
							attributes: { formId },
						});
						resolve(CloseFormResult.UnknownError);
					}
				},
			});
		});
	};
};
